<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="false"
    width="375px"
    top="32vh"
    custom-class="community-open-dialog"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    center
    @open="handleOpen"
  >
    <div slot="title">
      <h1>
        {{ $t('CommunityOpen.Title') }}
      </h1>
    </div>
    <el-form
      label-width="auto"
      @submit.native.prevent
    >
      <el-form-item :label="$t('CommunityOpen.CodeLabel')">
        <el-input
          ref="openCodeInput"
          v-model="openCode"
          autocomplete="off"
          @keyup.enter.native="sendOpenCode()"
        />
      </el-form-item>
    </el-form>
    <div class="hint-container">
      <p>{{ $t('CommunityOpen.GetCodeHint1') }}</p>
      <p>{{ $t('CommunityOpen.GetCodeHint2') }}</p>
      <a
        href="https://www.smartdaily.com.tw/faq/management/web/management-access/manager-get-activation-serial-number/"
        class="hint"
        target="_blank"
        rel="noopener"
      >{{ $t('CommunityOpen.LearnMoreLink') }}</a>
    </div>
    <div
      slot="footer"
      class="dialog-footer"
    >
      <el-button
        round
        @click="handleCancel"
      >
        {{ $t('General.Cancel') }}
      </el-button>
      <el-button
        type="primary"
        round
        @click="sendOpenCode"
      >
        {{ $t('General.OK') }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { apiCore } from '@/api/core.js'
export default {
  name: 'CommunityOpenDialog',
  props: {
    value: {
      // v-model 控制 dialog 是否顯示
      type: Boolean,
      required: true
    },
    token: {
      // 如果是在登入頁面調用，此時cookie還沒存入，所以需要傳入token
      // 如果是在其他頁面調用，則會自動將cookie加入headers，可留空
      type: String
    },
    isShowCancelAlert: {
      // 是否顯示「您的帳號未完成社區開通」提示
      type: Boolean,
      default: false
    },
    isReLoginAlert: {
      // 是否顯示「社區開通成功，若要切換社區請重新登入」提示
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      openCode: ''
    }
  },
  computed: {
    dialogVisible: {
      get () {
        return this.value
      },
      set (value) {
        this.$emit('input', value)
      }
    }
  },
  methods: {
    handleOpen () {
      this.openCode = ''
    },
    handleCancel () {
      if (this.isShowCancelAlert) {
        this.$alert(this.$t('CommunityOpen.NotOpenedMessage'), this.$t('CommunityOpen.NotOpenedTitle'), {
          confirmButtonText: this.$t('General.OK'),
          type: 'warning',
          customClass: 'community-open-cancel-alert',
          center: true,
          roundButton: true,
          showClose: false,
          dangerouslyUseHTMLString: true,
          message: `${this.$t('CommunityOpen.NotOpenedMessage')} <br> ${this.$t('CommunityOpen.NotOpenedHelp')}`
        }).then(() => {
          this.dialogVisible = false
          this.$emit('close')
        }).catch(() => {})
      } else {
        this.dialogVisible = false
        this.$emit('close')
      }
    },
    /**
     * 因為此處input (開通序號) 需要有enter事件
     * 但是 alert 的 enter 事件會穿透到input，導致重複送出alert
     * 因此需要先讓 input 失焦，避免 enter 事件穿透
     * 彈出 alert 後，alert 關閉時再將 focus 還回 input
     * @param {String} msg - alert 顯示訊息
     * @param {Object} opts - alert 其他選項（同 Element UI $alert）
     */
    showAlertWithInputBlur (msg, opts = {}) {
      // 1. 先讓 input 失焦，避免 enter 事件穿透
      this.$nextTick(() => {
        const input = this.$refs.openCodeInput?.$el?.querySelector('input')
        if (input) input.blur()
      })
      // 2. 彈出 alert，並於 callback 時 focus 回 input
      this.$alert(msg, '', {
        ...opts,
        callback: action => {
          // 3. alert 關閉後（不論點擊或 enter），延遲 focus 回 input
          setTimeout(() => {
            const input = this.$refs.openCodeInput?.$el?.querySelector('input')
            if (input) input.focus()
          }, 500)
          // 4. 若有自訂 callback，繼續執行
          if (opts.callback) opts.callback(action)
        }
      })
    },
    async sendOpenCode () {
      if (!this.openCode.length || !(/^[0-9A-Z]{8}$/.test(this.openCode))) {
        this.showAlertWithInputBlur(this.$t('CommunityOpen.InvalidCodeError'), {
          confirmButtonText: this.$t('General.OK'),
          type: 'error',
          showClose: false,
          callback: action => {}
        })
        return
      }

      const config = {
        metadata: {
          userHandlesErrors: true
        }
      }
      if (this.token) {
        // 這個組件在登入頁面調用時，根本還沒有Cookies
        // 需要額外傳入token
        config.headers = {
          Kingnetauth: `Cookies ${this.token}`
        }
      }

      try {
        await apiCore.useSnOpenAccount({
          Sn: this.openCode
        }, config)

        let msg = ''
        if (this.isReLoginAlert) {
          msg = this.$t('CommunityOpen.OpenSuccessMessageWithSwitch')
        } else {
          msg = this.$t('CommunityOpen.OpenSuccessMessage')
        }

        this.showAlertWithInputBlur(msg, {
          confirmButtonText: this.$t('General.OK'),
          showClose: false,
          type: 'success',
          callback: action => {
            if (action === 'confirm') {
              this.dialogVisible = false
              this.$emit('success')
            }
          }
        })
      } catch (err) {
        const message = err?.response?.data?.ErrorMessage?.Description
        if (message) {
          this.showAlertWithInputBlur(message, {
            confirmButtonText: this.$t('General.OK'),
            showClose: false,
            type: 'error',
            callback: action => {}
          })
        } else {
          this.$message.error(this.$t('Server.TryAgainLater'))
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
::v-deep(.community-open-dialog .el-dialog__header) {
  padding-top: 20px !important;
  padding-bottom: 0 !important;
}
::v-deep(.community-open-dialog .el-dialog__footer) {
  padding-top: 0 !important;
}

.community-open-dialog {
  display: flex;
  flex-direction: column;
  align-items: center;
  h1 {
    font-size: map-get($fontSize,large);
    font-weight: bold;
    text-align: center;
  }
  .hint-container {
    text-align: center;
    margin-top: 1rem;
    line-height: 1.3;
    a {
      color: $dangerColor;
      text-decoration: underline;
    }
  }
  ::v-deep .el-dialog__header {
    padding: 0 !important;
  }

  ::v-deep .el-dialog__body {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
}
</style>

<style>
.community-open-cancel-alert {
  .el-message-box__title {
    span {
      /* 蓋掉 element-variables.scss 的 width: 95% */
      width: auto !important;
      font-weight: bold;
    }
  }

  @media (max-width:768px) {
    /* 原本的寬度會是400px，在手機上會太寬 */
    width: 90vw !important;
  }
}
</style>
